import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  Tabs,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Tooltip,
  Checkbox,
  Progress,
  Typography,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Badge,
  Descriptions,
  Alert,
  Drawer,
  Divider,
  Empty,
  Spin,
  Radio,
  Dropdown
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  KeyOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  DesktopOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  SettingOutlined,
  ImportOutlined,
  LinkOutlined,
  DisconnectOutlined,
  CodeOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  LineChartOutlined,
  UnorderedListOutlined,
  EyeOutlined,
  PoweroffOutlined,
  ToolOutlined
} from '@ant-design/icons'
import nodeService from '../../services/nodeService'
import componentService, { componentLabel, errMsg } from '../../services/componentService'
import dayjs from 'dayjs'
import * as echarts from 'echarts'
import { Terminal } from 'xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import 'xterm/css/xterm.css'

const { Text } = Typography
const { TextArea } = Input

// 业务类型选项
const BUSINESS_TYPES = [
  { value: 'dns', label: 'DNS', color: 'blue' },
  { value: 'evm', label: 'EVM/KVM', color: 'purple' },
  { value: 'cdn', label: 'CDN', color: 'orange' },
  { value: 'host', label: 'Host', color: 'green' },
  { value: 'webmail', label: 'Webmail', color: 'cyan' },
  { value: 'mixed', label: 'Mixed', color: 'magenta' },
  { value: 'standalone', label: 'Standalone', color: 'default' },  // 纯 Agent 节点
]

// 状态选项
const STATUS_OPTIONS = [
  { value: 'online', label: '在线', color: 'green' },
  { value: 'offline', label: '离线', color: 'default' },
  { value: 'maintenance', label: '维护', color: 'orange' },
  { value: 'deploying', label: '部署中', color: 'blue' },
]

// 终端类型
const TERMINAL_TYPES = [
  { value: 'websocket', label: 'WebSocket' },
  { value: 'ssh', label: 'SSH' },
  { value: 'virsh', label: 'Virsh (KVM)' },
  { value: 'podman', label: 'Podman' },
]

// 任务状态
const TASK_STATUS = {
  pending: { label: '等待中', color: 'default' },
  running: { label: '执行中', color: 'processing' },
  success: { label: '成功', color: 'success' },
  failed: { label: '失败', color: 'error' },
}

// 节点监控与任务抽屉
const NodeMonitorDrawer = ({ open, node, onClose }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [hours, setHours] = useState(24)
  const [loading, setLoading] = useState(false)
  const [metrics, setMetrics] = useState(null)
  const [tasks, setTasks] = useState([])

  const loadData = useCallback(async () => {
    if (!node) return
    setLoading(true)
    try {
      const [m, t] = await Promise.all([
        nodeService.getNodeMetrics(node.id, { hours }),
        nodeService.getNodeTasks(node.id),
      ])
      setMetrics(m.data || null)
      setTasks(t.data || [])
    } catch (error) {
      message.error('加载监控数据失败')
    } finally {
      setLoading(false)
    }
  }, [node, hours])

  useEffect(() => {
    if (open) loadData()
  }, [open, loadData])

  // 渲染 CPU/内存/磁盘 历史曲线
  useEffect(() => {
    if (!open || !metrics || !chartRef.current) return
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current)
    }
    const history = metrics.history || []
    const times = history.map((h) => dayjs(h.recorded_at).format('MM-DD HH:mm'))
    chartInstance.current.setOption({
      tooltip: { trigger: 'axis' },
      legend: { data: ['CPU %', '内存 %', '磁盘 %'] },
      grid: { left: 44, right: 20, top: 40, bottom: 40 },
      xAxis: { type: 'category', boundaryGap: false, data: times },
      yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
      series: [
        {
          name: 'CPU %', type: 'line', smooth: true, showSymbol: false,
          itemStyle: { color: '#1890ff' },
          data: history.map((h) => Number(h.cpu_percent || 0).toFixed(1)),
        },
        {
          name: '内存 %', type: 'line', smooth: true, showSymbol: false,
          itemStyle: { color: '#52c41a' },
          data: history.map((h) => Number(h.memory_percent || 0).toFixed(1)),
        },
        {
          name: '磁盘 %', type: 'line', smooth: true, showSymbol: false,
          itemStyle: { color: '#faad14' },
          data: history.map((h) => Number(h.disk_percent || 0).toFixed(1)),
        },
      ],
    })
    chartInstance.current.resize()
  }, [open, metrics])

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose()
        chartInstance.current = null
      }
    }
  }, [])

  const averages = metrics?.averages || {}
  const taskColumns = [
    { title: '类型', dataIndex: 'type', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value) => {
        const s = TASK_STATUS[value]
        return s ? <Tag color={s.color}>{s.label}</Tag> : <Tag>{value}</Tag>
      },
    },
    {
      title: '进度',
      dataIndex: 'progress',
      width: 120,
      render: (value) => <Progress percent={value || 0} size="small" />,
    },
    { title: '信息', dataIndex: 'message', ellipsis: true },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (value) => (value ? dayjs(value).format('MM-DD HH:mm:ss') : '—'),
    },
  ]

  return (
    <Drawer
      title={
        <Space>
          <LineChartOutlined />
          <span>节点监控 - {node?.name || ''}</span>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={860}
      destroyOnClose
      extra={
        <Space>
          <Radio.Group value={hours} onChange={(e) => setHours(e.target.value)} size="small">
            <Radio.Button value={6}>6 小时</Radio.Button>
            <Radio.Button value={24}>24 小时</Radio.Button>
            <Radio.Button value={72}>3 天</Radio.Button>
          </Radio.Group>
          <Button icon={<ReloadOutlined />} size="small" onClick={loadData} />
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title="平均 CPU" value={averages.avg_cpu != null ? Number(averages.avg_cpu).toFixed(1) : '—'} suffix="%" />
          </Col>
          <Col span={6}>
            <Statistic title="平均内存" value={averages.avg_memory != null ? Number(averages.avg_memory).toFixed(1) : '—'} suffix="%" />
          </Col>
          <Col span={6}>
            <Statistic title="平均磁盘" value={averages.avg_disk != null ? Number(averages.avg_disk).toFixed(1) : '—'} suffix="%" />
          </Col>
          <Col span={6}>
            <Statistic title="采样数" value={averages.sample_count || 0} />
          </Col>
        </Row>

        {metrics && (metrics.history || []).length > 0 ? (
          <div ref={chartRef} style={{ width: '100%', height: 300 }} />
        ) : (
          <Empty description="暂无监控历史数据" style={{ padding: '32px 0' }} />
        )}

        <Divider orientation="left">
          <Space>
            <UnorderedListOutlined />
            任务历史
          </Space>
        </Divider>
        <Table
          rowKey="id"
          size="small"
          columns={taskColumns}
          dataSource={tasks}
          pagination={false}
          locale={{ emptyText: '暂无任务记录' }}
        />
      </Spin>
    </Drawer>
  )
}

// 分组节点下钻 Modal
const GroupNodesModal = ({ open, group, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [nodes, setNodes] = useState([])

  useEffect(() => {
    if (!open || !group) return
    setLoading(true)
    nodeService
      .getGroupNodes(group.id)
      .then((res) => {
        const data = res.data
        setNodes(Array.isArray(data) ? data : data?.nodes || [])
      })
      .catch(() => message.error('加载分组节点失败'))
      .finally(() => setLoading(false))
  }, [open, group])

  const columns = [
    { title: '节点名称', dataIndex: 'name' },
    {
      title: '业务类型',
      dataIndex: 'business_type',
      render: (value) => {
        const type = BUSINESS_TYPES.find((t) => t.value === value)
        return type ? <Tag color={type.color}>{type.label}</Tag> : value
      },
    },
    { title: 'IP 地址', dataIndex: 'ip_address' },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => {
        const s = STATUS_OPTIONS.find((o) => o.value === value)
        return s ? <Tag color={s.color}>{s.label}</Tag> : value
      },
    },
  ]

  return (
    <Modal
      title={`分组节点 - ${group?.name || ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={nodes}
        pagination={false}
        locale={{ emptyText: '该分组暂无节点' }}
      />
    </Modal>
  )
}

// 终端 Modal 组件
const TerminalModal = ({ visible, node, onClose }) => {
  const terminalRef = useRef(null)
  const terminalInstance = useRef(null)
  const fitAddon = useRef(null)
  const wsRef = useRef(null)
  const [connected, setConnected] = useState(false)
  const [status, setStatus] = useState('disconnected')

  useEffect(() => {
    if (!visible || !node) return

    // 创建终端实例
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: '"Cascadia Code", "Fira Code", Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#ffffff',
        cursorAccent: '#1e1e1e',
        selectionBackground: '#264f78',
        black: '#1e1e1e',
        red: '#f48771',
        green: '#89d185',
        yellow: '#dcdcaa',
        blue: '#569cd6',
        magenta: '#c586c0',
        cyan: '#4ec9b0',
        white: '#d4d4d4',
      },
      scrollback: 10000,
      allowProposedApi: true,
    })

    fitAddon.current = new FitAddon()
    term.loadAddon(fitAddon.current)
    term.loadAddon(new WebLinksAddon())

    // 延迟挂载，等待 DOM 准备好
    const mountTimer = setTimeout(() => {
      if (terminalRef.current) {
        term.open(terminalRef.current)
        fitAddon.current.fit()
        terminalInstance.current = term

        // 连接 WebSocket
        connectWebSocket(term, node.id)
      }
    }, 100)

    // 监听窗口大小变化
    const handleResize = () => {
      if (fitAddon.current && terminalInstance.current) {
        fitAddon.current.fit()
        sendResize()
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(mountTimer)
      window.removeEventListener('resize', handleResize)
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      if (terminalInstance.current) {
        terminalInstance.current.dispose()
        terminalInstance.current = null
      }
    }
  }, [visible, node])

  const connectWebSocket = (term, nodeId) => {
    const token = localStorage.getItem('auth_token')
    // 使用 API 域名而不是当前页面域名
    const apiHost = process.env.REACT_APP_API_BASE_URL || 'https://api2.gtm.cx'
    const wsProtocol = apiHost.startsWith('https') ? 'wss:' : 'ws:'
    const apiDomain = apiHost.replace(/^https?:\/\//, '')
    const wsUrl = `${wsProtocol}//${apiDomain}/api/node/terminal/ws?node_id=${nodeId}&token=${token}`

    setStatus('connecting')
    term.writeln('\x1b[33m[系统] 正在连接到节点...\x1b[0m')

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setConnected(true)
      setStatus('connected')
      term.writeln('\x1b[32m[系统] WebSocket 已连接\x1b[0m')
      // 发送 resize
      setTimeout(sendResize, 100)
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        switch (msg.type) {
          case 'connected':  // PHP 发送 connected 表示终端已就绪
          case 'terminal_ready':
            term.writeln('\x1b[32m[系统] 终端已就绪\x1b[0m')
            setStatus('ready')
            break
          case 'data':  // PHP 发送 data 类型
          case 'terminal_data':
            if (msg.data) {
              // Base64 解码
              const decoded = atob(msg.data)
              term.write(decoded)
            }
            break
          case 'error':
            term.writeln(`\x1b[31m[错误] ${msg.message || '未知错误'}\x1b[0m`)
            setStatus('error')
            break
          case 'disconnected':
            term.writeln(`\x1b[31m[系统] ${msg.message || '连接已断开'}\x1b[0m`)
            setStatus('disconnected')
            setConnected(false)
            break
          case 'pong':
            // 心跳响应，忽略
            break
          default:
            console.log('Unknown message type:', msg.type, msg)
        }
      } catch (e) {
        // 非 JSON 数据直接写入
        term.write(event.data)
      }
    }

    ws.onclose = () => {
      setConnected(false)
      setStatus('disconnected')
      term.writeln('\x1b[33m[系统] 连接已关闭\x1b[0m')
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      setStatus('error')
      term.writeln('\x1b[31m[错误] WebSocket 连接失败\x1b[0m')
    }

    // 终端输入发送到 WebSocket
    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'data',
          data: btoa(data),  // Base64 编码
        }))
      }
    })

    // 心跳
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000)

    return () => {
      clearInterval(pingInterval)
    }
  }

  const sendResize = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && terminalInstance.current) {
      const { cols, rows } = terminalInstance.current
      wsRef.current.send(JSON.stringify({
        type: 'resize',
        cols,
        rows,
      }))
    }
  }

  const handleModalResize = () => {
    setTimeout(() => {
      if (fitAddon.current && terminalInstance.current) {
        fitAddon.current.fit()
        sendResize()
      }
    }, 100)
  }

  const statusColor = {
    disconnected: 'default',
    connecting: 'blue',
    connected: 'green',
    ready: 'green',
    error: 'red',
  }

  const statusText = {
    disconnected: '已断开',
    connecting: '连接中...',
    connected: '已连接',
    ready: '就绪',
    error: '错误',
  }

  return (
    <Modal
      title={
        <Space>
          <CodeOutlined />
          <span>终端 - {node?.name || ''}</span>
          <Tag color={statusColor[status]}>{statusText[status]}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width="85%"
      style={{ top: 20 }}
      destroyOnClose
      afterOpenChange={(open) => {
        if (open) handleModalResize()
      }}
      styles={{
        body: { padding: 0, height: '75vh', background: '#1e1e1e', overflow: 'hidden' }
      }}
    >
      <div
        ref={terminalRef}
        style={{ width: '100%', height: '100%', padding: '8px' }}
      />
    </Modal>
  )
}

const NodeManager = () => {
  const [activeTab, setActiveTab] = useState('nodes')
  const [overview, setOverview] = useState(null)

  const loadOverview = async () => {
    try {
      const response = await nodeService.getOverview()
      setOverview(response.data || response)
    } catch (error) {
      console.error('Load overview failed:', error)
    }
  }

  useEffect(() => {
    loadOverview()
  }, [])

  return (
    <Card
      title={
        <Space>
          <CloudServerOutlined />
          <span>统一节点管理</span>
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={loadOverview}>
          刷新概览
        </Button>
      }
    >
      {overview && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Statistic
              title="总节点数"
              value={overview.total || overview.total_nodes || 0}
              prefix={<CloudServerOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="在线节点"
              value={overview.online || overview.online_nodes || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="离线节点"
              value={overview.offline || overview.offline_nodes || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="维护中"
              value={overview.maintenance || overview.maintenance_nodes || 0}
              valueStyle={{ color: '#d4b106' }}
              prefix={<SettingOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic title="分组数" value={overview.total_groups || 0} prefix={<ClusterOutlined />} />
          </Col>
          <Col span={4}>
            <Statistic
              title="平均CPU"
              value={overview.avg_cpu != null ? Number(overview.avg_cpu).toFixed(1) : '0.0'}
              suffix="%"
              valueStyle={{
                color:
                  (overview.avg_cpu || 0) > 80 ? '#cf1322' : (overview.avg_cpu || 0) > 60 ? '#d4b106' : '#3f8600'
              }}
            />
          </Col>
        </Row>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="节点列表" key="nodes" icon={<CloudServerOutlined />}>
          <NodeListManager />
        </Tabs.TabPane>
        <Tabs.TabPane tab="分组管理" key="groups" icon={<ClusterOutlined />}>
          <GroupManager />
        </Tabs.TabPane>
        <Tabs.TabPane tab="导入业务节点" key="sync" icon={<ImportOutlined />}>
          <SyncManager />
        </Tabs.TabPane>
      </Tabs>
    </Card>
  )
}

const NodeListManager = () => {
  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [terminalVisible, setTerminalVisible] = useState(false)
  const [terminalNode, setTerminalNode] = useState(null)
  const [editingNode, setEditingNode] = useState(null)
  const [viewingNode, setViewingNode] = useState(null)
  const [groups, setGroups] = useState([])
  const [filters, setFilters] = useState({})
  const [businessNodes, setBusinessNodes] = useState([])
  const [businessNodesLoading, setBusinessNodesLoading] = useState(false)
  const [deployModalVisible, setDeployModalVisible] = useState(false)
  const [deployingNode, setDeployingNode] = useState(null)
  const [deployTask, setDeployTask] = useState(null)
  const [deployTitle, setDeployTitle] = useState('部署进度')
  const deployPollRef = useRef(null)
  const deployPollFailRef = useRef(0)
  const resetHostKeyRef = useRef(false)
  const [monitorVisible, setMonitorVisible] = useState(false)
  const [monitorNode, setMonitorNode] = useState(null)
  const [credentialsVisible, setCredentialsVisible] = useState(false)
  const [credentials, setCredentials] = useState(null)
  const [credentialsLoading, setCredentialsLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [batchLoading, setBatchLoading] = useState(false)
  const [form] = Form.useForm()

  const loadNodes = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      // 表格在前端分页:一次取全部(后端默认每页 20,之前超过 20 个节点的部分不显示)
      const response = await nodeService.getNodes({ ...filters, page_size: 500 })
      // 兼容分页格式 {data: {nodes: [], total: ...}} 和数组格式 {data: []}
      const rawData = response.data
      if (Array.isArray(rawData)) {
        setNodes(rawData)
      } else if (rawData && rawData.nodes) {
        setNodes(rawData.nodes)
      } else {
        setNodes([])
      }
    } catch (error) {
      if (!silent) message.error('加载节点列表失败')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [filters])

  const loadGroups = async () => {
    try {
      const response = await nodeService.getGroups()
      setGroups(response.data || response.groups || [])
    } catch (error) {
      console.error('Load groups failed:', error)
    }
  }

  // 加载业务节点列表
  const loadBusinessNodes = async (businessType) => {
    if (!businessType || businessType === 'standalone' || businessType === 'mixed') {
      setBusinessNodes([])
      return
    }
    setBusinessNodesLoading(true)
    try {
      const response = await nodeService.getBusinessNodes(businessType)
      setBusinessNodes(response.data || [])
    } catch (error) {
      console.error('Load business nodes failed:', error)
      setBusinessNodes([])
    } finally {
      setBusinessNodesLoading(false)
    }
  }

  // 处理业务类型变化
  const handleBusinessTypeChange = (value) => {
    form.setFieldValue('business_node_id', null)
    loadBusinessNodes(value)
  }

  const [autoRefresh, setAutoRefresh] = useState(true)

  useEffect(() => {
    loadNodes()
    loadGroups()
  }, [loadNodes])

  // 各组件最新版本(组件列 tooltip / 更新菜单用)
  const [latestVersions, setLatestVersions] = useState({})
  useEffect(() => {
    componentService
      .list()
      .then((res) => {
        const map = {}
        ;(res.data?.components || []).forEach((c) => {
          if (c.latest) map[c.component] = c.latest.version
        })
        setLatestVersions(map)
      })
      .catch(() => {})
  }, [])

  // 自动刷新 - 每 5 秒静默刷新
  useEffect(() => {
    if (!autoRefresh) return
    const timer = setInterval(() => {
      loadNodes(true)
    }, 5000)
    return () => clearInterval(timer)
  }, [autoRefresh, loadNodes])

  const handleCreate = () => {
    setEditingNode(null)
    setBusinessNodes([])
    form.resetFields()
    form.setFieldsValue({
      status: 'offline',
      business_type: 'standalone',  // 默认纯 Agent 节点
      terminal_enabled: true,
      terminal_type: 'ssh',
      monitor_enabled: true,
      ssh_port: 22,
      ssh_user: 'root',
      terminal_port: 22,
    })
    setModalVisible(true)
  }

  const handleEdit = async (record) => {
    try {
      const response = await nodeService.getNode(record.id)
      const node = response.data || response.node || response
      setEditingNode(node)
      form.setFieldsValue({
        ...node,
        group_id: node.group?.id || node.group_id || null,
      })
      // 加载对应业务类型的节点列表
      if (node.business_type && node.business_type !== 'standalone' && node.business_type !== 'mixed') {
        loadBusinessNodes(node.business_type)
      } else {
        setBusinessNodes([])
      }
      setModalVisible(true)
    } catch (error) {
      message.error('获取节点详情失败')
    }
  }

  const handleView = async (record) => {
    try {
      const response = await nodeService.getNode(record.id)
      setViewingNode(response.data || response.node || response)
      setDetailModalVisible(true)
    } catch (error) {
      message.error('获取节点详情失败')
    }
  }

  const handleDelete = async (record) => {
    try {
      await nodeService.deleteNode(record.id)
      message.success('节点已删除')
      loadNodes()
    } catch (error) {
      message.error(error?.response?.data?.error || '删除失败')
    }
  }

  // 批量操作
  const handleBatch = async (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择节点')
      return
    }
    setBatchLoading(true)
    try {
      const response = await nodeService.batchAction({ action, node_ids: selectedRowKeys })
      message.success(response.msg || '批量操作完成')
      setSelectedRowKeys([])
      loadNodes()
    } catch (error) {
      message.error(error?.response?.data?.msg || '批量操作失败')
    } finally {
      setBatchLoading(false)
    }
  }

  // 查看 SSH 凭据
  const handleViewCredentials = async (record) => {
    setCredentials(null)
    setCredentialsVisible(true)
    setCredentialsLoading(true)
    try {
      const response = await nodeService.getNodeCredentials(record.id)
      setCredentials(response.data || null)
    } catch (error) {
      message.error('获取凭据失败')
    } finally {
      setCredentialsLoading(false)
    }
  }

  // 解绑业务节点
  const handleUnbind = async (record) => {
    try {
      await nodeService.unbindBusinessNode(record.id)
      message.success('已解绑业务节点')
      setDetailModalVisible(false)
      loadNodes()
    } catch (error) {
      message.error(error?.response?.data?.msg || '解绑失败')
    }
  }

  const handleRegenerateKey = async (record) => {
    try {
      const response = await nodeService.regenerateNodeKey(record.id)
      Modal.success({
        title: '新密钥生成成功',
        content: (
          <div>
            <p>请保存新的 Agent 密钥：</p>
            <Input.TextArea
              value={response.data?.agent_key || response.agent_key}
              rows={2}
              style={{ fontFamily: 'monospace' }}
            />
            <Alert
              message="注意：旧密钥已失效，请立即更新节点 Agent 配置"
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          </div>
        ),
        width: 500,
      })
      loadNodes()
    } catch (error) {
      message.error('重新生成密钥失败')
    }
  }

  // 轮询部署任务状态
  const pollDeployTask = useCallback(async (taskId) => {
    try {
      const response = await nodeService.getTaskStatus(taskId)
      const task = response.data
      deployPollFailRef.current = 0
      setDeployTask(task)

      // 如果任务完成，停止轮询
      if (task.status === 'success' || task.status === 'failed') {
        if (deployPollRef.current) {
          clearInterval(deployPollRef.current)
          deployPollRef.current = null
        }
        if (task.status === 'success') {
          loadNodes()
        }
      }
    } catch (error) {
      // 连续失败就停止轮询并提示,避免无限转圈
      deployPollFailRef.current += 1
      if (deployPollFailRef.current >= 5 && deployPollRef.current) {
        clearInterval(deployPollRef.current)
        deployPollRef.current = null
        message.error('获取部署进度失败，请稍后在任务记录中查看')
      }
    }
  }, [loadNodes])

  // 清理轮询
  useEffect(() => {
    return () => {
      if (deployPollRef.current) {
        clearInterval(deployPollRef.current)
      }
    }
  }, [])

  // 一键部署 Agent
  const handleDeploy = async (record) => {
    if (!record.has_credentials) {
      message.warning('该节点未配置 SSH 凭据，无法一键部署')
      return
    }
    resetHostKeyRef.current = false

    Modal.confirm({
      title: `一键部署 - ${record.name}`,
      width: 600,
      content: (
        <div>
          <Alert
            message={`将通过 SSH 远程部署 ${record.business_type.toUpperCase()} 类型的 Agent`}
            description={`目标: ${record.ip_address} (${record.ssh_user || 'root'}@${record.ssh_port || 22})`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>部署将执行以下操作：</p>
          <ul>
            <li>下载并安装对应业务类型的 Agent</li>
            <li>生成配置文件</li>
            <li>创建 systemd 服务</li>
            <li>启动服务</li>
          </ul>
          <Checkbox onChange={(e) => { resetHostKeyRef.current = e.target.checked }}>
            <Tooltip title="节点重装或换机后 SSH 主机密钥会变，勾选后重新记录">重置主机密钥</Tooltip>
          </Checkbox>
        </div>
      ),
      okText: '开始部署',
      cancelText: '取消',
      onOk: async () => {
        try {
          deployPollFailRef.current = 0
          const response = await nodeService.deployNode(record.id, {
            bridge_mode: record.business_type === 'evm' ? 'linux' : undefined,
            reset_host_key: resetHostKeyRef.current ? 1 : undefined,
          })

          if (response.status === 'success' && response.data?.task_id) {
            // 设置初始任务状态
            setDeployTitle('部署进度')
            setDeployingNode(record)
            setDeployTask({
              id: response.data.task_id,
              status: 'pending',
              progress: 0,
              message: response.data.message || '任务已创建',
              output: '',
            })
            setDeployModalVisible(true)

            // 开始轮询
            deployPollRef.current = setInterval(() => {
              pollDeployTask(response.data.task_id)
            }, 1500)
          }
        } catch (error) {
          const errorData = error?.response?.data

          // 如果有正在执行的任务，显示该任务的进度
          if (errorData?.data?.task_id) {
            message.info('该节点有正在执行的任务，正在显示进度...')
            setDeployTitle('部署进度')
            setDeployingNode(record)
            setDeployTask({
              id: errorData.data.task_id,
              status: 'running',
              progress: 0,
              message: '加载中...',
              output: '',
            })
            setDeployModalVisible(true)

            // 立即获取一次状态
            pollDeployTask(errorData.data.task_id)

            // 开始轮询
            deployPollRef.current = setInterval(() => {
              pollDeployTask(errorData.data.task_id)
            }, 1500)
          } else {
            message.error(errorData?.msg || '创建部署任务失败')
          }
        }
      },
    })
  }

  // 打开任务进度弹窗并轮询(组件更新 / 安装 Agent 复用部署进度弹窗)
  const openTaskProgress = (record, taskId, title, initMsg = '任务已创建') => {
    if (deployPollRef.current) {
      clearInterval(deployPollRef.current)
      deployPollRef.current = null
    }
    deployPollFailRef.current = 0
    setDeployTitle(title)
    setDeployingNode(record)
    setDeployTask({ id: taskId, status: 'pending', progress: 0, message: initMsg, output: '' })
    setDeployModalVisible(true)
    pollDeployTask(taskId)
    deployPollRef.current = setInterval(() => pollDeployTask(taskId), 1500)
  }

  // 组件热更新(最新版本)
  const handleComponentUpdate = async (record, component, force = false) => {
    try {
      const res = await componentService.updateNode(record.id, { component, force: force || undefined })
      const task = res.data?.task
      if (task?.id) {
        openTaskProgress(record, task.id, `更新 ${componentLabel(component)}`, task.message || '任务已创建')
      }
    } catch (e) {
      if (e?.response?.status === 409 && !force) {
        Modal.confirm({
          title: '强制更新?',
          content: errMsg(e),
          okText: '强制更新',
          okButtonProps: { danger: true },
          cancelText: '取消',
          onOk: () => handleComponentUpdate(record, component, true),
        })
      } else {
        message.error(errMsg(e, '更新失败'))
      }
    }
  }

  // 只安装统一 yeap-agent
  const handleInstallAgent = (record) => {
    if (!record.has_credentials) {
      message.warning('该节点未配置 SSH 凭据')
      return
    }
    Modal.confirm({
      title: `安装 Agent - ${record.name}`,
      content: `${record.ip_address} 将通过 SSH 安装 yeap-agent`,
      okText: '安装',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await nodeService.deployNode(record.id, { agent_only: 1 })
          if (res.data?.task_id) {
            openTaskProgress(record, res.data.task_id, '安装 Agent', res.data.message || '任务已创建')
          }
        } catch (e) {
          const tid = e?.response?.data?.data?.task_id
          if (tid) {
            message.info('该节点有正在执行的任务，正在显示进度...')
            openTaskProgress(record, tid, '任务进度', '加载中...')
          } else {
            message.error(errMsg(e, '创建安装任务失败'))
          }
        }
      },
    })
  }

  // 关闭部署模态框
  const handleCloseDeployModal = () => {
    if (deployPollRef.current) {
      clearInterval(deployPollRef.current)
      deployPollRef.current = null
    }
    setDeployModalVisible(false)
    setDeployingNode(null)
    setDeployTask(null)
  }

  // 获取部署脚本
  const handleGetDeployScript = async (record) => {
    try {
      const response = await nodeService.getDeployScript(record.id)
      Modal.info({
        title: `部署脚本 - ${record.name} (${record.business_type})`,
        width: 800,
        content: (
          <div>
            <Alert
              message="可在节点上手动执行以下脚本完成部署"
              description={`UUID: ${response.data?.node_uuid} | Agent Key: ${response.data?.agent_key?.substring(0, 16)}...`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ maxHeight: 400, overflow: 'auto', background: '#1e1e1e', padding: 12, borderRadius: 4 }}>
              <pre style={{ color: '#d4d4d4', margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {response.data?.script || '无法生成脚本'}
              </pre>
            </div>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              style={{ marginTop: 12 }}
              onClick={() => {
                navigator.clipboard.writeText(response.data?.script || '')
                message.success('脚本已复制到剪贴板')
              }}
            >
              复制脚本
            </Button>
          </div>
        ),
        okText: '关闭',
      })
    } catch (error) {
      message.error('获取部署脚本失败')
    }
  }

  const submitNode = async () => {
    try {
      const values = await form.validateFields()
      if (editingNode) {
        await nodeService.updateNode(editingNode.id, values)
        message.success('节点已更新')
      } else {
        const response = await nodeService.createNode(values)
        message.success('节点已创建')
        // 显示初始密钥
        if (response.data?.agent_key || response.agent_key) {
          Modal.success({
            title: '节点创建成功',
            content: (
              <div>
                <p>
                  <strong>UUID:</strong> {response.data?.uuid || response.uuid}
                </p>
                <p>
                  <strong>Agent Key:</strong>
                </p>
                <Input.TextArea
                  value={response.data?.agent_key || response.agent_key}
                  rows={2}
                  style={{ fontFamily: 'monospace' }}
                />
                <Alert
                  message="请保存此密钥，它仅显示一次"
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            ),
            width: 500,
          })
        }
      }
      setModalVisible(false)
      loadNodes()
    } catch (error) {
      if (error?.errorFields) return
      message.error(error?.response?.data?.error || error?.message || '保存失败')
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    message.success('已复制到剪贴板')
  }

  const columns = [
    {
      title: '节点名称',
      dataIndex: 'name',
      render: (text, record) => {
        // Standalone 类型根据 agent_status 判断状态
        let badgeStatus = 'error'
        if (record.business_type === 'standalone') {
          badgeStatus = record.agent_status === 'online' ? 'success' : 'default'
        } else {
          badgeStatus = record.status === 'online' ? 'success'
            : record.status === 'maintenance' ? 'warning' : 'error'
        }
        return (
          <Space>
            <Badge status={badgeStatus} />
            <a onClick={() => handleView(record)}>{text}</a>
          </Space>
        )
      },
    },
    {
      title: '业务类型',
      dataIndex: 'business_type',
      render: (value) => {
        const type = BUSINESS_TYPES.find((t) => t.value === value)
        return type ? <Tag color={type.color}>{type.label}</Tag> : value
      },
      filters: BUSINESS_TYPES.map((t) => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.business_type === value,
    },
    {
      title: '组件',
      dataIndex: 'components',
      render: (list) => {
        if (!list || !list.length) return <Text type="secondary">—</Text>
        return (
          <Space size={[2, 2]} wrap style={{ maxWidth: 220 }}>
            {list.map((c) => {
              const text = `${componentLabel(c.component)} ${c.version || '?'}`
              if (!c.active) {
                return <Tooltip key={c.component} title="未运行"><Tag color="red" style={{ fontSize: 11, marginInlineEnd: 0 }}>{text}</Tag></Tooltip>
              }
              if (c.outdated) {
                return (
                  <Tooltip key={c.component} title={`最新: ${latestVersions[c.component] || '?'}`}>
                    <Tag color="orange" style={{ fontSize: 11, marginInlineEnd: 0 }}>{text}</Tag>
                  </Tooltip>
                )
              }
              return <Tag key={c.component} style={{ fontSize: 11, marginInlineEnd: 0 }}>{text}</Tag>
            })}
          </Space>
        )
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value, record) => {
        // Standalone 类型只看 Agent 状态
        if (record.business_type === 'standalone') {
          const agentOnline = record.agent_status === 'online'
          return (
            <Tag color={agentOnline ? 'green' : 'default'}>
              {agentOnline ? 'Agent 在线' : 'Agent 离线'}
            </Tag>
          )
        }
        // 其他类型显示节点状态 + Agent 状态
        return (
          <Space direction="vertical" size={0}>
            <Tag color={value === 'online' ? 'green' : value === 'maintenance' ? 'orange' : 'default'}>
              {STATUS_OPTIONS.find((s) => s.value === value)?.label || value}
            </Tag>
            {record.agent_status && (
              <Tag
                color={record.agent_status === 'online' ? 'cyan' : 'default'}
                style={{ fontSize: 10 }}
              >
                Agent: {record.agent_status === 'online' ? '在线' : '离线'}
              </Tag>
            )}
          </Space>
        )
      },
      filters: STATUS_OPTIONS.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text copyable={{ text }}>{text}</Text>
          {record.probe_platform && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.probe_platform}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'CPU',
      dataIndex: 'last_cpu',
      sorter: (a, b) => (a.last_cpu || 0) - (b.last_cpu || 0),
      render: (value) =>
        value !== null ? (
          <Progress
            percent={parseFloat(value?.toFixed(1)) || 0}
            size="small"
            status={value > 80 ? 'exception' : value > 60 ? 'active' : 'normal'}
            strokeColor={value > 80 ? '#ff4d4f' : value > 60 ? '#faad14' : '#52c41a'}
          />
        ) : (
          '—'
        ),
    },
    {
      title: '内存',
      dataIndex: 'last_memory',
      sorter: (a, b) => (a.last_memory || 0) - (b.last_memory || 0),
      render: (value) =>
        value !== null && value !== undefined ? (
          <Progress
            percent={parseFloat(value?.toFixed(1)) || 0}
            size="small"
            status={value > 80 ? 'exception' : value > 60 ? 'active' : 'normal'}
            strokeColor={value > 80 ? '#ff4d4f' : value > 60 ? '#faad14' : '#52c41a'}
          />
        ) : (
          '—'
        ),
    },
    {
      title: '硬盘',
      dataIndex: 'last_disk',
      sorter: (a, b) => (a.last_disk || 0) - (b.last_disk || 0),
      render: (value) =>
        value !== null && value !== undefined ? (
          <Progress
            percent={parseFloat(value?.toFixed(1)) || 0}
            size="small"
            status={value > 90 ? 'exception' : value > 80 ? 'active' : 'normal'}
            strokeColor={value > 90 ? '#ff4d4f' : value > 80 ? '#faad14' : '#52c41a'}
          />
        ) : (
          '—'
        ),
    },
    {
      title: '网络',
      dataIndex: 'metrics',
      render: (metrics) => {
        if (!metrics || (metrics.net_in_speed === 0 && metrics.net_out_speed === 0)) {
          return '—'
        }
        const formatSpeed = (bytes) => {
          if (bytes < 1024) return `${bytes} B/s`
          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB/s`
          return `${(bytes / 1024 / 1024).toFixed(1)} MB/s`
        }
        return (
          <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
            <span style={{ color: '#52c41a' }}>↓ {formatSpeed(metrics.net_in_speed || 0)}</span>
            <span style={{ color: '#1890ff' }}>↑ {formatSpeed(metrics.net_out_speed || 0)}</span>
          </Space>
        )
      },
    },
    {
      title: '最后心跳',
      dataIndex: 'last_heartbeat',
      sorter: (a, b) =>
        new Date(a.last_heartbeat || 0).getTime() - new Date(b.last_heartbeat || 0).getTime(),
      render: (value) => (value ? dayjs(value).format('MM-DD HH:mm:ss') : '—'),
    },
    {
      title: '凭据',
      dataIndex: 'has_credentials',
      render: (value) =>
        value ? (
          <Tag color="green" icon={<KeyOutlined />}>
            已配置
          </Tag>
        ) : (
          <Tag color="default">未配置</Tag>
        ),
    },
    {
      title: '终端',
      dataIndex: 'terminal_enabled',
      render: (value) =>
        value ? (
          <Tag color="green" icon={<DesktopOutlined />}>
            已启用
          </Tag>
        ) : (
          <Tag color="default">未启用</Tag>
        ),
    },
    {
      title: '操作',
      width: 280,
      render: (_, record) => (
        <Space size="small" wrap>
          {(record.components || []).some((c) => c.outdated) && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: record.components
                  .filter((c) => c.outdated)
                  .map((c) => ({
                    key: c.component,
                    label: `${componentLabel(c.component)} ${c.version || '?'} → ${latestVersions[c.component] || '最新'}`,
                  })),
                onClick: ({ key }) => handleComponentUpdate(record, key),
              }}
            >
              <Button size="small" icon={<SyncOutlined />} style={{ color: '#fa8c16', borderColor: '#fa8c16' }}>
                更新
              </Button>
            </Dropdown>
          )}
          {/* Standalone 节点不显示一键部署 */}
          {record.business_type !== 'standalone' && record.has_credentials && (
            <Tooltip title="一键部署">
              <Button
                icon={<CloudUploadOutlined />}
                size="small"
                type="primary"
                onClick={() => handleDeploy(record)}
              />
            </Tooltip>
          )}
          {/* Standalone 节点不显示部署脚本 */}
          {record.business_type !== 'standalone' && (
            <Tooltip title="部署脚本">
              <Button
                icon={<DownloadOutlined />}
                size="small"
                onClick={() => handleGetDeployScript(record)}
              />
            </Tooltip>
          )}
          {/* 终端按钮 - status 或 agent_status 在线都可以打开终端 */}
          {(record.status === 'online' || record.agent_status === 'online') && (
            <Tooltip title="打开终端">
              <Button
                icon={<CodeOutlined />}
                size="small"
                type="primary"
                onClick={() => {
                  setTerminalNode(record)
                  setTerminalVisible(true)
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="安装 Agent">
            <Button icon={<ToolOutlined />} size="small" onClick={() => handleInstallAgent(record)} />
          </Tooltip>
          <Tooltip title="监控与任务">
            <Button
              icon={<LineChartOutlined />}
              size="small"
              onClick={() => {
                setMonitorNode(record)
                setMonitorVisible(true)
              }}
            />
          </Tooltip>
          <Tooltip title="详情">
            <Button icon={<DesktopOutlined />} size="small" onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="重新生成密钥">
            <Popconfirm
              title="确定要重新生成 Agent 密钥吗？"
              description="这将使旧密钥失效，节点需要更新配置"
              onConfirm={() => handleRegenerateKey(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button icon={<KeyOutlined />} size="small" />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定要删除此节点吗？"
              onConfirm={() => handleDelete(record)}
              okText="删除"
              cancelText="取消"
            >
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          添加节点
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadNodes}>
          刷新
        </Button>
        <Switch
          checked={autoRefresh}
          onChange={setAutoRefresh}
          checkedChildren="自动刷新"
          unCheckedChildren="手动刷新"
        />
        <Select
          placeholder="业务类型筛选"
          allowClear
          style={{ width: 150 }}
          options={BUSINESS_TYPES}
          onChange={(value) => setFilters((prev) => ({ ...prev, business_type: value }))}
        />
        <Select
          placeholder="状态筛选"
          allowClear
          style={{ width: 120 }}
          options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
        />
      </Space>

      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 16, marginLeft: 8 }}>
          <span>已选 {selectedRowKeys.length} 项：</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            loading={batchLoading}
            onClick={() => handleBatch('set_online')}
          >
            设为在线
          </Button>
          <Button
            size="small"
            icon={<PoweroffOutlined />}
            loading={batchLoading}
            onClick={() => handleBatch('set_offline')}
          >
            设为离线
          </Button>
          <Button
            size="small"
            icon={<ToolOutlined />}
            loading={batchLoading}
            onClick={() => handleBatch('set_maintenance')}
          >
            设为维护
          </Button>
          <Popconfirm
            title={`确定要删除选中的 ${selectedRowKeys.length} 个节点吗？`}
            onConfirm={() => handleBatch('delete')}
            okText="删除"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />} loading={batchLoading}>
              批量删除
            </Button>
          </Popconfirm>
        </Space>
      )}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={nodes}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 个节点`,
        }}
      />

      {/* 编辑/创建 Modal */}
      <Modal
        title={editingNode ? '编辑节点' : '添加节点'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={submitNode}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="节点名称"
                name="name"
                rules={[{ required: true, message: '请输入节点名称' }]}
              >
                <Input placeholder="dns-node-01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="业务类型"
                name="business_type"
                rules={[{ required: true, message: '请选择业务类型' }]}
              >
                <Select options={BUSINESS_TYPES} onChange={handleBusinessTypeChange} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="IP 地址"
                name="ip_address"
                rules={[{ required: true, message: '请输入IP地址' }]}
              >
                <Input placeholder="192.168.1.100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="内网 IP" name="internal_ip">
                <Input placeholder="10.0.0.100" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="状态" name="status" initialValue="offline">
                <Select
                  options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="分组" name="group_id">
                <Select
                  allowClear
                  options={groups.map((g) => ({
                    value: g.id,
                    label: g.name,
                  }))}
                  placeholder="选择分组"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="SSH 密码"
                name="ssh_password"
                tooltip="用于 SSH 连接认证"
              >
                <Input.Password placeholder="SSH 登录密码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="SSH 端口" name="ssh_port">
                <InputNumber style={{ width: '100%' }} min={1} max={65535} placeholder="22" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="SSH 私钥"
            name="ssh_key"
            tooltip="用于 SSH 密钥认证，粘贴完整的私钥内容"
          >
            <TextArea rows={3} placeholder="-----BEGIN RSA PRIVATE KEY-----..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="启用终端" name="terminal_enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="终端类型" name="terminal_type">
                <Select options={TERMINAL_TYPES} placeholder="选择终端类型" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="终端端口" name="terminal_port">
                <InputNumber style={{ width: '100%' }} min={1} max={65535} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="启用监控" name="monitor_enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="CPU 核心数" name="cpu_cores">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="内存 (GB)" name="memory_total">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="关联业务节点"
            name="business_node_id"
            tooltip="选择要关联的业务节点，仅在选择具体业务类型后可选"
          >
            <Select
              allowClear
              showSearch
              loading={businessNodesLoading}
              disabled={businessNodes.length === 0}
              placeholder={businessNodes.length === 0 ? '请先选择业务类型' : '选择要关联的业务节点'}
              optionFilterProp="label"
              options={businessNodes.map((node) => ({
                value: node.id,
                label: `${node.name} (${node.ip_address})${node.bound ? ' - 已绑定' : ''}`,
                disabled: node.bound,
              }))}
            />
          </Form.Item>

          <Form.Item label="备注" name="remarks">
            <TextArea rows={2} placeholder="节点备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情 Modal */}
      <Modal
        title="节点详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={700}
        footer={
          viewingNode ? (
            <Space>
              <Button icon={<EyeOutlined />} onClick={() => handleViewCredentials(viewingNode)}>
                查看 SSH 凭据
              </Button>
              {viewingNode.business_node_id &&
                viewingNode.business_type !== 'standalone' && (
                  <Popconfirm
                    title="确定要解绑该业务节点吗？"
                    description="解绑后统一节点将不再关联对应业务节点"
                    onConfirm={() => handleUnbind(viewingNode)}
                    okText="解绑"
                    cancelText="取消"
                  >
                    <Button danger icon={<DisconnectOutlined />}>
                      解绑业务节点
                    </Button>
                  </Popconfirm>
                )}
              <Button type="primary" onClick={() => setDetailModalVisible(false)}>
                关闭
              </Button>
            </Space>
          ) : null
        }
      >
        {viewingNode && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="名称">{viewingNode.name}</Descriptions.Item>
            <Descriptions.Item label="UUID">
              <Text copyable>{viewingNode.uuid}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="业务类型">
              {BUSINESS_TYPES.find((t) => t.value === viewingNode.business_type)?.label ||
                viewingNode.business_type}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {STATUS_OPTIONS.find((s) => s.value === viewingNode.status)?.label ||
                viewingNode.status}
            </Descriptions.Item>
            <Descriptions.Item label="IP 地址">{viewingNode.ip_address}</Descriptions.Item>
            <Descriptions.Item label="内网 IP">{viewingNode.internal_ip || '—'}</Descriptions.Item>
            <Descriptions.Item label="CPU">
              {viewingNode.last_cpu?.toFixed(1)}% ({viewingNode.cpu_cores} 核)
            </Descriptions.Item>
            <Descriptions.Item label="内存">
              {viewingNode.last_memory?.toFixed(1)}% ({viewingNode.memory_total} GB)
            </Descriptions.Item>
            <Descriptions.Item label="磁盘">{viewingNode.last_disk?.toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label="负载">{viewingNode.last_load?.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="终端">
              {viewingNode.terminal_enabled ? '已启用' : '未启用'}
              {viewingNode.terminal_enabled && ` (${viewingNode.terminal_type}:${viewingNode.terminal_port})`}
            </Descriptions.Item>
            <Descriptions.Item label="监控">
              {viewingNode.monitor_enabled ? '已启用' : '未启用'}
            </Descriptions.Item>
            <Descriptions.Item label="Agent 版本">{viewingNode.agent_version || '—'}</Descriptions.Item>
            <Descriptions.Item label="Agent 功能">
              {viewingNode.agent_features?.join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="最后心跳" span={2}>
              {viewingNode.last_heartbeat
                ? dayjs(viewingNode.last_heartbeat).format('YYYY-MM-DD HH:mm:ss')
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(viewingNode.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {dayjs(viewingNode.updated_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>
              {viewingNode.remarks || '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 部署进度模态框 */}
      <Modal
        title={`${deployTitle} - ${deployingNode?.name || ''}`}
        open={deployModalVisible}
        onCancel={handleCloseDeployModal}
        footer={
          deployTask?.status === 'success' || deployTask?.status === 'failed' ? (
            <Button type="primary" onClick={handleCloseDeployModal}>
              关闭
            </Button>
          ) : null
        }
        width={700}
        maskClosable={false}
      >
        {deployTask && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Badge
                  status={
                    deployTask.status === 'success'
                      ? 'success'
                      : deployTask.status === 'failed'
                      ? 'error'
                      : deployTask.status === 'running'
                      ? 'processing'
                      : 'default'
                  }
                  text={
                    deployTask.status === 'success'
                      ? '部署成功'
                      : deployTask.status === 'failed'
                      ? '部署失败'
                      : deployTask.status === 'running'
                      ? '部署中'
                      : '等待中'
                  }
                />
              </Space>
            </div>

            <Progress
              percent={deployTask.progress}
              status={
                deployTask.status === 'success'
                  ? 'success'
                  : deployTask.status === 'failed'
                  ? 'exception'
                  : 'active'
              }
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 8 }}>
              <Text type="secondary">{deployTask.message}</Text>
            </div>

            {deployTask.error && (
              <Alert
                message="错误信息"
                description={deployTask.error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {deployTask.output && (
              <div
                style={{
                  maxHeight: 300,
                  overflow: 'auto',
                  background: '#1e1e1e',
                  padding: 12,
                  borderRadius: 4,
                }}
              >
                <pre
                  style={{
                    color: deployTask.status === 'failed' ? '#f48771' : '#d4d4d4',
                    margin: 0,
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {deployTask.output}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 终端模态框 */}
      <TerminalModal
        visible={terminalVisible}
        node={terminalNode}
        onClose={() => {
          setTerminalVisible(false)
          setTerminalNode(null)
        }}
      />

      {/* 监控与任务抽屉 */}
      <NodeMonitorDrawer
        open={monitorVisible}
        node={monitorNode}
        onClose={() => {
          setMonitorVisible(false)
          setMonitorNode(null)
        }}
      />

      {/* SSH 凭据 Modal */}
      <Modal
        title="SSH 凭据（敏感信息）"
        open={credentialsVisible}
        onCancel={() => setCredentialsVisible(false)}
        footer={
          <Button type="primary" onClick={() => setCredentialsVisible(false)}>
            关闭
          </Button>
        }
        width={640}
      >
        <Spin spinning={credentialsLoading}>
          {credentials ? (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="IP 地址">{credentials.ip_address || '—'}</Descriptions.Item>
              <Descriptions.Item label="内网 IP">{credentials.internal_ip || '—'}</Descriptions.Item>
              <Descriptions.Item label="SSH 端口">{credentials.ssh_port || 22}</Descriptions.Item>
              <Descriptions.Item label="SSH 用户">{credentials.ssh_user || 'root'}</Descriptions.Item>
              <Descriptions.Item label="SSH 密码">
                {credentials.has_password ? (
                  <Text copyable={{ text: credentials.ssh_password }}>
                    {credentials.ssh_password}
                  </Text>
                ) : (
                  <Tag>未配置</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="SSH 私钥">
                {credentials.has_key ? (
                  <Input.TextArea
                    value={credentials.ssh_key}
                    rows={4}
                    readOnly
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                ) : (
                  <Tag>未配置</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            !credentialsLoading && <Empty description="无凭据信息" />
          )}
        </Spin>
      </Modal>
    </>
  )
}

const GroupManager = () => {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [groupNodesVisible, setGroupNodesVisible] = useState(false)
  const [viewingGroup, setViewingGroup] = useState(null)
  const [form] = Form.useForm()

  const loadGroups = async () => {
    setLoading(true)
    try {
      const response = await nodeService.getGroups()
      setGroups(response.data || response.groups || [])
    } catch (error) {
      message.error('加载分组失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadGroups()
  }, [])

  const handleCreate = () => {
    setEditingGroup(null)
    form.resetFields()
    form.setFieldsValue({
      status: 'active',
      sort_order: 0,
    })
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    setEditingGroup(record)
    form.setFieldsValue(record)
    setModalVisible(true)
  }

  const handleDelete = async (record) => {
    try {
      await nodeService.deleteGroup(record.id)
      message.success('分组已删除')
      loadGroups()
    } catch (error) {
      message.error(error?.response?.data?.error || '删除失败')
    }
  }

  const submitGroup = async () => {
    try {
      const values = await form.validateFields()
      if (editingGroup) {
        await nodeService.updateGroup(editingGroup.id, values)
        message.success('分组已更新')
      } else {
        await nodeService.createGroup(values)
        message.success('分组已创建')
      }
      setModalVisible(false)
      loadGroups()
    } catch (error) {
      if (error?.errorFields) return
      message.error('保存失败')
    }
  }

  const columns = [
    {
      title: '分组名称',
      dataIndex: 'name',
      render: (text, record) => (
        <Space>
          {record.icon && <span>{record.icon}</span>}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '业务类型',
      dataIndex: 'business_types',
      render: (value) =>
        value?.map((t) => {
          const type = BUSINESS_TYPES.find((bt) => bt.value === t)
          return type ? (
            <Tag key={t} color={type.color}>
              {type.label}
            </Tag>
          ) : (
            <Tag key={t}>{t}</Tag>
          )
        }) || <Tag>全部</Tag>,
    },
    {
      title: '父分组',
      dataIndex: 'parent',
      render: (parent) => parent?.name || '—',
    },
    {
      title: '节点数',
      dataIndex: 'nodes_count',
      sorter: (a, b) => (a.nodes_count || 0) - (b.nodes_count || 0),
      render: (value, record) => (
        <a
          onClick={() => {
            setViewingGroup(record)
            setGroupNodesVisible(true)
          }}
        >
          {value ?? record.node_count ?? 0}
        </a>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      sorter: (a, b) => a.sort_order - b.sort_order,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => (
        <Tag color={value === 'active' ? 'green' : 'default'}>
          {value === 'active' ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button
            icon={<UnorderedListOutlined />}
            size="small"
            onClick={() => {
              setViewingGroup(record)
              setGroupNodesVisible(true)
            }}
          >
            查看节点
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此分组吗？"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          创建分组
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadGroups}>
          刷新
        </Button>
      </Space>

      <Table rowKey="id" loading={loading} columns={columns} dataSource={groups} />

      <Modal
        title={editingGroup ? '编辑分组' : '创建分组'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={submitGroup}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="分组名称"
            name="name"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="DNS 节点组" />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <TextArea rows={2} placeholder="分组描述" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="业务类型" name="business_types">
                <Select
                  mode="multiple"
                  options={BUSINESS_TYPES}
                  placeholder="选择业务类型（不选表示全部）"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="父分组" name="parent_id">
                <Select
                  allowClear
                  options={groups
                    .filter((g) => g.id !== editingGroup?.id)
                    .map((g) => ({ value: g.id, label: g.name }))}
                  placeholder="选择父分组"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="图标" name="icon">
                <Input placeholder="图标 emoji" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="颜色" name="color">
                <Input type="color" style={{ width: 60 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="排序" name="sort_order" initialValue={0}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="状态" name="status" initialValue="active">
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'archived', label: '停用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <GroupNodesModal
        open={groupNodesVisible}
        group={viewingGroup}
        onClose={() => {
          setGroupNodesVisible(false)
          setViewingGroup(null)
        }}
      />
    </>
  )
}

const SyncManager = () => {
  const [availableNodes, setAvailableNodes] = useState([])
  const [syncStats, setSyncStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [filterType, setFilterType] = useState(null)
  const [innerTab, setInnerTab] = useState('available')
  const [syncedNodes, setSyncedNodes] = useState([])
  const [syncedLoading, setSyncedLoading] = useState(false)

  const loadAvailableNodes = async () => {
    setLoading(true)
    try {
      const params = filterType ? { business_type: filterType } : {}
      const response = await nodeService.getAvailableNodes(params)
      setAvailableNodes(response.data || [])
    } catch (error) {
      message.error('加载可用节点失败')
    } finally {
      setLoading(false)
    }
  }

  const loadSyncStats = async () => {
    try {
      const response = await nodeService.getSyncStats()
      setSyncStats(response.data || {})
    } catch (error) {
      console.error('Load sync stats failed:', error)
    }
  }

  const loadSyncedNodes = async () => {
    setSyncedLoading(true)
    try {
      const params = filterType ? { business_type: filterType } : {}
      const response = await nodeService.getSyncedNodes(params)
      setSyncedNodes(response.data || [])
    } catch (error) {
      message.error('加载已同步节点失败')
    } finally {
      setSyncedLoading(false)
    }
  }

  const handleUnsync = async (record) => {
    try {
      await nodeService.unsync(record.id)
      message.success('已取消导入')
      loadSyncedNodes()
      loadSyncStats()
    } catch (error) {
      message.error(error?.response?.data?.msg || '取消导入失败')
    }
  }

  useEffect(() => {
    loadAvailableNodes()
    loadSyncStats()
    if (innerTab === 'synced') loadSyncedNodes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, innerTab])

  const handleSyncOne = async (record) => {
    setSyncing(true)
    try {
      await nodeService.syncOne({
        business_type: record.business_type,
        business_node_id: record.business_node_id,
      })
      message.success('同步成功')
      loadAvailableNodes()
      loadSyncStats()
    } catch (error) {
      message.error(error?.response?.data?.msg || '同步失败')
    } finally {
      setSyncing(false)
    }
  }

  const handleSyncBatch = async () => {
    if (selectedRows.length === 0) {
      message.warning('请选择要同步的节点')
      return
    }

    setSyncing(true)
    try {
      const nodes = selectedRows.map((row) => ({
        business_type: row.business_type,
        business_node_id: row.business_node_id,
      }))
      const response = await nodeService.syncBatch({ nodes })
      message.success(response.msg || '批量同步完成')
      setSelectedRows([])
      loadAvailableNodes()
      loadSyncStats()
    } catch (error) {
      message.error(error?.response?.data?.msg || '批量同步失败')
    } finally {
      setSyncing(false)
    }
  }

  const handleSyncAll = async () => {
    setSyncing(true)
    try {
      const payload = filterType ? { business_type: filterType } : {}
      const response = await nodeService.syncAll(payload)
      message.success(response.msg || '全部同步完成')
      loadAvailableNodes()
      loadSyncStats()
    } catch (error) {
      message.error(error?.response?.data?.msg || '同步失败')
    } finally {
      setSyncing(false)
    }
  }

  const columns = [
    {
      title: '业务类型',
      dataIndex: 'business_type',
      width: 100,
      render: (value) => {
        const type = BUSINESS_TYPES.find((t) => t.value === value)
        return type ? <Tag color={type.color}>{type.label}</Tag> : value
      },
    },
    {
      title: '节点名称',
      dataIndex: 'name',
    },
    {
      title: 'IP 地址',
      dataIndex: 'ip_address',
      render: (text) => <Text copyable={{ text }}>{text}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => {
        const status = STATUS_OPTIONS.find((s) => s.value === value)
        return status ? <Tag color={status.color}>{status.label}</Tag> : value
      },
    },
    {
      title: '操作',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<LinkOutlined />}
          loading={syncing}
          onClick={() => handleSyncOne(record)}
        >
          导入
        </Button>
      ),
    },
  ]

  const syncedColumns = [
    {
      title: '业务类型',
      dataIndex: 'business_type',
      width: 100,
      render: (value) => {
        const type = BUSINESS_TYPES.find((t) => t.value === value)
        return type ? <Tag color={type.color}>{type.label}</Tag> : value
      },
    },
    { title: '节点名称', dataIndex: 'name' },
    {
      title: 'IP 地址',
      dataIndex: 'ip_address',
      render: (text) => <Text copyable={{ text }}>{text}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => {
        const status = STATUS_OPTIONS.find((s) => s.value === value)
        return status ? <Tag color={status.color}>{status.label}</Tag> : value
      },
    },
    {
      title: '操作',
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="确定要取消导入该节点吗？"
          description="取消后该节点将从统一节点管理中移除"
          onConfirm={() => handleUnsync(record)}
          okText="取消导入"
          cancelText="取消"
        >
          <Button danger size="small" icon={<DisconnectOutlined />}>
            取消导入
          </Button>
        </Popconfirm>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys: selectedRows.map(
      (r) => `${r.business_type}-${r.business_node_id}`
    ),
    onChange: (_, rows) => setSelectedRows(rows),
  }

  return (
    <>
      {syncStats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {Object.entries(syncStats).map(([type, stats]) => {
            const typeInfo = BUSINESS_TYPES.find((t) => t.value === type)
            return (
              <Col span={4} key={type}>
                <Card size="small">
                  <Statistic
                    title={typeInfo?.label || type}
                    value={stats.synced}
                    suffix={`/ ${stats.total}`}
                    valueStyle={{
                      color: stats.unsynced > 0 ? '#d4b106' : '#3f8600',
                      fontSize: 20,
                    }}
                  />
                  {stats.unsynced > 0 && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                      {stats.unsynced} 未同步
                    </Text>
                  )}
                </Card>
              </Col>
            )
          })}
        </Row>
      )}

      <Tabs activeKey={innerTab} onChange={setInnerTab}>
        <Tabs.TabPane tab="可导入" key="available">
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder="筛选业务类型"
              allowClear
              style={{ width: 150 }}
              options={BUSINESS_TYPES.filter((t) => t.value !== 'standalone' && t.value !== 'mixed')}
              onChange={(value) => setFilterType(value)}
            />
            <Button icon={<ReloadOutlined />} onClick={loadAvailableNodes}>
              刷新
            </Button>
            <Button
              type="primary"
              icon={<ImportOutlined />}
              onClick={handleSyncBatch}
              disabled={selectedRows.length === 0}
              loading={syncing}
            >
              批量导入 ({selectedRows.length})
            </Button>
            <Popconfirm
              title="确定要导入所有未同步的节点吗？"
              onConfirm={handleSyncAll}
              okText="确定"
              cancelText="取消"
            >
              <Button icon={<SyncOutlined />} loading={syncing}>
                全部导入
              </Button>
            </Popconfirm>
          </Space>

          <Alert
            message="导入说明"
            description="导入业务节点会将现有的 EVM/CDN/Host/Webmail/DNS 节点接入统一节点管理系统。导入后可以使用统一的监控、终端和分组功能。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            rowKey={(record) => `${record.business_type}-${record.business_node_id}`}
            loading={loading}
            columns={columns}
            dataSource={availableNodes}
            rowSelection={rowSelection}
            pagination={{
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 个可导入节点`,
            }}
          />
        </Tabs.TabPane>

        <Tabs.TabPane tab="已同步" key="synced">
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder="筛选业务类型"
              allowClear
              style={{ width: 150 }}
              options={BUSINESS_TYPES.filter((t) => t.value !== 'standalone' && t.value !== 'mixed')}
              onChange={(value) => setFilterType(value)}
            />
            <Button icon={<ReloadOutlined />} onClick={loadSyncedNodes}>
              刷新
            </Button>
          </Space>

          <Table
            rowKey="id"
            loading={syncedLoading}
            columns={syncedColumns}
            dataSource={syncedNodes}
            pagination={{
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 个已同步节点`,
            }}
          />
        </Tabs.TabPane>
      </Tabs>
    </>
  )
}

export default NodeManager
